/**
 * Employee create/edit write path (#22335) -- backend allowlist entries
 * (`payroll.createEmployee`/`payroll.updateEmployee`, both
 * `payroll:employer:write`-scoped) were already merged in #22724/PR #22734;
 * this hook is the first thing that actually calls them.
 *
 * Mirrors `use_pay_runs.ts`'s write-path shape exactly: `isWriteCapableGrant`
 * (imported from there, not re-implemented -- one predicate, one place it can
 * drift) gates both `create`/`update`, both are pessimistic (state only
 * changes after the mutation resolves), and neither ever throws -- every
 * failure resolves a classified `EmployeeMutationResult` instead.
 *
 * ── Failure classification (four buckets, not two) ─────────────────────────
 *
 * `payroll.createEmployee`/`updateEmployee` can fail in more distinguishable
 * ways than `use_pay_runs.ts`'s two (`entitlement_required`/`error`):
 *
 *   - `plan_cap` -- the per-entity payroll free-employee allowance
 *     (Lite/Starter 3, Growth 5, Scale 10) was reached
 *     (`assertPayrollEmployeeWithinPlanCap`, `apps/api/src/lib/
 *     plan_cap_gate.ts`). Classified via
 *     `isPlanPayrollEmployeeCapExceededError` (ledgerCode-based, not message
 *     text) -- this is the ONE failure this form must never show as a raw
 *     error toast (#22335 acceptance criterion): it is an expected,
 *     actionable state ("remove an employee or upgrade"), not a bug.
 *   - `entitlement_required` -- the entity has no active `payroll` add-on
 *     entitlement (`requirePayrollAddon`) -- mirrors every other C-child's
 *     `entitlement_required` state.
 *   - `validation` -- `validateEmployeeProfile`/`assertValidNicAndEpf`
 *     rejected the input server-side (a cross-field rule the client-side
 *     `employee_form_validation.ts` mirror missed, or a race between two
 *     concurrent edits). Carries the backend's own caller-safe `message` --
 *     see `isBadRequestTrpcError`'s doc comment in `lib/trpc.ts` for why that
 *     message is safe to display verbatim.
 *   - `worker_type` -- a worker-type refusal tagged with a `ledgerCode`
 *     (switching someone on a draft pay run, or an employee with history,
 *     to contractor; an FTE for a
 *     non-part-timer). Checked BEFORE `validation`: both are BAD_REQUEST, but
 *     this one carries a stable code, so the form renders a translated
 *     message (`workerTypeCodeMessage(ledger_code, t)`) rather than the
 *     backend's English text.
 *   - `employee_number_taken` -- the `employee_number` is already held by
 *     another employee of the organisation (CONFLICT,
 *     `LEDGER_PAYROLL_EMPLOYEE_NUMBER_TAKEN`, MIGRATION_V395). Rendered with
 *     a translated message, never the backend's English text.
 *   - `error` -- anything else (network, an unexpected FORBIDDEN, a 500).
 */
import {
  getTrpcClient,
  isBadRequestTrpcError,
  isEmployeeNumberTakenError,
  isPayrollAddonRequiredError,
  isPlanPayrollEmployeeCapExceededError,
  workerTypeErrorCode,
} from '../lib/trpc.js';
import { useAuth } from '../lib/auth/auth_context.js';
import { useOAuthEntityContext } from '../lib/auth/oauth_entity_context.js';
import { isWriteCapableGrant, type PayRunsFetchInput } from './use_pay_runs.js';
import type {
  CreateEmployeeInput,
  UpdateEmployeeInput,
} from '../screens/employees/employee_form_validation.js';

/** Same entity-scoping shape every `payroll.*` call takes -- reused from `use_pay_runs.ts` rather than redeclared. */
export type EmployeeMutationEntityInput = PayRunsFetchInput;

export type EmployeeMutationFailure =
  | { kind: 'plan_cap'; message: string }
  | { kind: 'entitlement_required' }
  | { kind: 'worker_type'; ledger_code: string }
  | { kind: 'employee_number_taken' }
  | { kind: 'validation'; message: string }
  | { kind: 'error' };

export type EmployeeMutationResult =
  | { outcome: 'success'; employee_id: string }
  | { outcome: 'failure'; failure: EmployeeMutationFailure };

/** Classifies a caught `createEmployee`/`updateEmployee` error into one of the {@link EmployeeMutationFailure} buckets -- see module docblock. Exported for unit tests. */
export function classifyEmployeeMutationFailure(
  err: unknown,
): EmployeeMutationFailure {
  if (isPlanPayrollEmployeeCapExceededError(err)) {
    return {
      kind: 'plan_cap',
      message: err instanceof Error ? err.message : '',
    };
  }
  if (isPayrollAddonRequiredError(err)) {
    return { kind: 'entitlement_required' };
  }
  if (isEmployeeNumberTakenError(err)) {
    return { kind: 'employee_number_taken' };
  }
  const workerTypeCode = workerTypeErrorCode(err);
  if (workerTypeCode !== null) {
    return { kind: 'worker_type', ledger_code: workerTypeCode };
  }
  if (isBadRequestTrpcError(err)) {
    return {
      kind: 'validation',
      message: err instanceof Error ? err.message : '',
    };
  }
  return { kind: 'error' };
}

/** The two `payroll.*` calls this hook needs, isolated behind an injectable seam -- mirrors `use_pay_runs.ts`'s `PayRunMutationDeps` DI pattern. */
export interface EmployeeMutationDeps {
  createEmployee: (
    input: EmployeeMutationEntityInput & CreateEmployeeInput,
  ) => Promise<{ employee_id: string }>;
  updateEmployee: (
    input: EmployeeMutationEntityInput & {
      employee_id: string;
    } & UpdateEmployeeInput,
  ) => Promise<{ ok: boolean }>;
}

function defaultEmployeeMutationDeps(): EmployeeMutationDeps {
  const client = getTrpcClient();
  return {
    createEmployee: (input) => client.payroll.createEmployee.mutate(input),
    updateEmployee: (input) => client.payroll.updateEmployee.mutate(input),
  };
}

/**
 * Calls the real `payroll.createEmployee`. Never throws -- see module
 * docblock. `deps` is resolved INSIDE the `try` (never as a default
 * parameter value), same reasoning as `use_pay_runs.ts`'s `startPayRun`: a
 * synchronous `getTrpcClient()` throw must not escape as an unhandled
 * rejection past this "never throws" function's contract.
 */
export async function createEmployeeRecord(
  input: CreateEmployeeInput,
  entityInput: EmployeeMutationEntityInput,
  deps?: EmployeeMutationDeps,
): Promise<EmployeeMutationResult> {
  try {
    const resolvedDeps = deps ?? defaultEmployeeMutationDeps();
    const result = await resolvedDeps.createEmployee({
      ...entityInput,
      ...input,
    });
    return { outcome: 'success', employee_id: result.employee_id };
  } catch (err) {
    console.error('[wagepro-web] createEmployee failed:', err);
    return {
      outcome: 'failure',
      failure: classifyEmployeeMutationFailure(err),
    };
  }
}

/** Calls the real `payroll.updateEmployee`. Never throws -- same contract as {@link createEmployeeRecord}. */
export async function updateEmployeeRecord(
  employee_id: string,
  input: UpdateEmployeeInput,
  entityInput: EmployeeMutationEntityInput,
  deps?: EmployeeMutationDeps,
): Promise<EmployeeMutationResult> {
  try {
    const resolvedDeps = deps ?? defaultEmployeeMutationDeps();
    await resolvedDeps.updateEmployee({
      ...entityInput,
      employee_id,
      ...input,
    });
    return { outcome: 'success', employee_id };
  } catch (err) {
    console.error('[wagepro-web] updateEmployee failed:', err);
    return {
      outcome: 'failure',
      failure: classifyEmployeeMutationFailure(err),
    };
  }
}

export interface UseEmployeeMutationsResult {
  /** `undefined` when the write path isn't reachable -- no bound entity, or the OAuth grant isn't write-capable (see `is_read_only_grant`). */
  create:
    | ((input: CreateEmployeeInput) => Promise<EmployeeMutationResult>)
    | undefined;
  /** `undefined` for the same reasons as {@link create}. */
  update:
    | ((
        employee_id: string,
        input: UpdateEmployeeInput,
      ) => Promise<EmployeeMutationResult>)
    | undefined;
  /** `true` when the OAuth grant is NOT write-capable -- `!isWriteCapableGrant(grant)`, mirrors `use_pay_runs.ts`'s `is_read_only_grant`. The Add/Edit affordances must be hidden entirely (not just disabled) when this is `true` (#22335 acceptance criterion). */
  is_read_only_grant: boolean;
}

/**
 * The employee create/edit write path, gated identically to
 * `use_pay_runs.ts`'s `start_pay_run`/`sign_off`: reachable only with a
 * bound entity AND a write-capable OAuth grant (`mode !== 'read'` AND
 * `scopes` actually includes `payroll:employer:write`).
 */
export function useEmployeeMutations(): UseEmployeeMutationsResult {
  const entityContext = useOAuthEntityContext();
  const { grant } = useAuth();

  // `readyEntity` narrows `entityContext` once, up front -- `canWrite`'s own
  // declaration references it directly, so TS's aliased-condition narrowing
  // then treats `readyEntity` as definitely non-null inside any branch
  // already gated on `canWrite` below, without re-checking
  // `entityContext.status === 'ready'` a second time (mirrors
  // `use_pay_runs.ts`'s identical `readyEntity`/`canWrite` pattern).
  const readyEntity = entityContext.status === 'ready' ? entityContext : null;
  const canWrite = readyEntity !== null && isWriteCapableGrant(grant);

  const create = canWrite
    ? (input: CreateEmployeeInput) =>
        createEmployeeRecord(input, {
          firm_id: readyEntity.firm_id,
          entity_id: readyEntity.entity_id,
        })
    : undefined;

  const update = canWrite
    ? (employee_id: string, input: UpdateEmployeeInput) =>
        updateEmployeeRecord(employee_id, input, {
          firm_id: readyEntity.firm_id,
          entity_id: readyEntity.entity_id,
        })
    : undefined;

  return {
    create,
    update,
    is_read_only_grant: !isWriteCapableGrant(grant),
  };
}
